/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { CluTrainedModelStore } from 'src/clu/core/stores/CluTrainedModelStore';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisText } from 'src/core/components/LuisText';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ResourceAssignmentForRegionSwagger } from 'src/core/services/resourceAssignmentSwaggers';
import { CustomSkillDeploymentStore } from 'src/core/stores/CustomSkillDeploymentStore';
import { ResourceAssignmentStore } from 'src/core/stores/ResourceAssignmentStore';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { FormValidators } from 'src/core/utils/formValidators';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { CustomTextTrainedModelStore } from 'src/customText/core/stores/CustomTextTrainedModelStore';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { DeploymentInfoScreen } from 'src/modules/modelDeploy/DeploymentInfoScreen';

export type DeploymentType = 'new' | 'existing';
export type DeployModelModalProps = BasicModalProps<{
    deploymentName: string;
    modelName: string;
    resources: ResourceAssignmentForRegionSwagger[];
}> & {
    isServiceKeyTypeValid: boolean;
    isMultiRegionDeploymentEnabled?: boolean;
    isOrchestrationProjectConnectedToLUISOrQna: boolean;
    regionDeploymentStoreHouse?: ResourceStoreHouse<ResourceAssignmentStore>;
    deploymentsStoreHouse: ResourceStoreHouse<CustomSkillDeploymentStore | CustomSkillDeploymentStore>;
    trainedModelStoreHouse: ResourceStoreHouse<CluTrainedModelStore | CustomTextTrainedModelStore>;
};

const messages = defineMessages({
    deployModalButton: { id: 'modelDeploy.deployModelModal.deployModalButton', defaultMessage: 'Deploy' },
    deployModalTitle: { id: 'modelDeploy.deployModelModal.deployModalTitle', defaultMessage: 'Add deployment' },
    deploymentOptionsTitle: {
        id: 'modelDeploy.deployModelModal.deploymentOptionsTitle',
        defaultMessage: 'Create or select an existing deployment name'
    },
    duplicatedNameErrorMessage: {
        id: 'modelDeploy.deployModelModal.duplicatedNameErrorMessage',
        defaultMessage: 'This name already exists.'
    },
    nameErrorMessage: {
        id: 'modelDeploy.deployModelModal.nameErrorMessage',
        defaultMessage: 'Invalid characters: You can only use "-", "_", "." and alphanumeric characters.'
    },
    deployModalBody: {
        id: 'modelDeploy.deployModelModal.deployModalBody',
        defaultMessage: 'Deploying the model will make it available for use via the endpoint. It will replace any existing deployed model.'
    }
});

export const DeployModelModal = withModalMounter(
    observer((props: DeployModelModalProps) => {
        const {
            hidden,
            isServiceKeyTypeValid,
            deploymentsStoreHouse,
            trainedModelStoreHouse,
            regionDeploymentStoreHouse,
            isOrchestrationProjectConnectedToLUISOrQna,
            isMultiRegionDeploymentEnabled = FeatureToggles.MultiRegionDeployments,
            onSubmit,
            onDismiss,
            ...rest
        } = props;
        const { localize } = useLocalization();

        const [selectedResources, setSelectedResources] = React.useState<ResourceAssignmentForRegionSwagger[]>([]);

        const { formState, formStateDto, updateFormState } = useFormState<{
            deploymentName: string;
            trainedModelName: string;
        }>({
            trainedModelName: ['', FormValidators.createRequiredValidator()],
            deploymentName: [
                '',
                FormValidators.createRequiredValidator(),
                FormValidators.createBlacklistValidator({
                    mode: 'include',
                    errorMessage: messages.nameErrorMessage,
                    characterList: LuisConstants.ResourceLimits.AllowedNameReges
                })
            ]
        });
        const [selectedDeploymentType, setSelectedDeploymentType] = React.useState<DeploymentType>('new');

        const deployments = deploymentsStoreHouse.items;
        const regions = regionDeploymentStoreHouse.items;
        const trainedModels = trainedModelStoreHouse.items;
        const isDuplicateName = selectedDeploymentType === 'new' && deployments.some(d => d.deploymentName === formStateDto.deploymentName);

        const onDeploymentTypeChange = (newType: DeploymentType) => {
            updateFormState({ deploymentName: '' });
            setSelectedDeploymentType(newType);
        };

        return (
            <BasicModal
                {...rest}
                minWidth={750}
                hidden={hidden}
                onDismiss={onDismiss}
                valid={formState.form.valid && !isDuplicateName}
                doneButtonText={localize(messages.deployModalButton)}
                onSubmit={() =>
                    onSubmit({
                        resources: selectedResources,
                        modelName: formStateDto.trainedModelName,
                        deploymentName: formStateDto.deploymentName.trim()
                    })
                }
                dialogContentProps={{
                    title: localize(messages.deployModalTitle),
                    styles: { innerContent: { height: '100%' } }
                }}>
                <Stack tokens={{ childrenGap: 5 }}>
                    <LuisText variant="mediumPlus" fontWeight={600}>
                        {localize(messages.deploymentOptionsTitle)}
                    </LuisText>

                    <DeploymentInfoScreen
                        regions={regions}
                        deployments={deployments}
                        trainedModels={trainedModels}
                        deploymentName={formStateDto.deploymentName}
                        isServiceKeyTypeValid={isServiceKeyTypeValid}
                        selectedDeploymentType={selectedDeploymentType}
                        onDeploymentTypeChange={onDeploymentTypeChange}
                        trainedModelName={formStateDto.trainedModelName}
                        isMultiRegionDeploymentEnabled={isMultiRegionDeploymentEnabled}
                        onChangeResourceIds={resources => setSelectedResources(resources)}
                        onChangeDeploymentName={name => updateFormState({ deploymentName: name })}
                        onChangeTrainedModelName={name => updateFormState({ trainedModelName: name })}
                        deploymentNameErrorMessage={
                            formState.fields.deploymentName.errorMessage ||
                            (isDuplicateName && localize(messages.duplicatedNameErrorMessage))
                        }
                        isOrchestrationProjectConnectedToLUISOrQna={isOrchestrationProjectConnectedToLUISOrQna}
                    />
                </Stack>
            </BasicModal>
        );
    })
);
